import React, { useMemo, useState } from 'react';
import { useProducts } from '../ProductContext';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';

const InventoryReportPage = () => {
  const { products, inventoryLoading } = useProducts();
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const q = filter.trim().toLowerCase();
    return list.filter((p) => !q || String(p.name || '').toLowerCase().includes(q) || String(p.category || '').toLowerCase().includes(q));
  }, [products, filter]);

  const invTotals = useMemo(
    () =>
      rows.reduce(
        (acc, p) => {
          const q = Number(p.quantity) || 0;
          const buy = Number(p.purchasingPrice) || 0;
          const sell = Number(p.sellingPrice) || 0;
          return {
            qty: acc.qty + q,
            atCost: acc.atCost + q * buy,
            atSell: acc.atSell + q * sell,
          };
        },
        { qty: 0, atCost: 0, atSell: 0 }
      ),
    [rows]
  );

  const exportHead = ['Product', 'Category', 'Stock', 'Purchase TZS', 'Sell TZS', 'Low limit'];
  const exportRows = useMemo(
    () =>
      rows.map((p) => [
        p.name,
        p.category || '—',
        p.quantity,
        p.purchasingPrice ?? '',
        p.sellingPrice ?? '',
        p.lowStockLimit ?? '',
      ]),
    [rows]
  );

  const exportRowsWithTotals = useMemo(() => {
    if (rows.length === 0) return exportRows;
    return [
      ...exportRows,
      [
        'TOTALS (filtered)',
        '',
        invTotals.qty,
        Math.round(invTotals.atCost),
        Math.round(invTotals.atSell),
        '',
      ],
    ];
  }, [exportRows, rows.length, invTotals]);

  if (inventoryLoading) {
    return (
      <div className="page-bleed" style={{ background: '#f4f6fa', minHeight: '100vh', paddingTop: 32, paddingBottom: 40 }}>
        <h1 style={{ color: '#2d3748', fontWeight: 700 }}>Inventory Report</h1>
        <p style={{ color: '#64748b' }}>Loading products…</p>
      </div>
    );
  }

  return (
    <div className="page-bleed" style={{ background: '#f4f6fa', minHeight: '100vh', paddingTop: 32, paddingBottom: 40 }}>
      <h1 style={{ color: '#2d3748', fontWeight: 700 }}>Inventory Report</h1>
      <p style={{ color: '#64748b', marginBottom: 8 }}>Live data from approved products ({rows.length} shown).</p>
      <p style={{ color: '#0f766e', fontWeight: 600, marginBottom: 16 }}>
        Filtered totals — units: <strong>{invTotals.qty.toLocaleString()}</strong>
        {' · '}
        stock at cost: <strong>TZS {Math.round(invTotals.atCost).toLocaleString()}</strong>
        {' · '}
        at sell price: <strong>TZS {Math.round(invTotals.atSell).toLocaleString()}</strong>
      </p>
      <div style={{ marginBottom: 20 }}>
        <label htmlFor="inv-filter">Filter by name or category: </label>
        <input
          id="inv-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Type to filter…"
          style={{ marginLeft: 8, padding: '8px 12px', minWidth: 240 }}
        />
      </div>
      <table style={{ background: '#fff', borderRadius: 8, width: '100%', padding: 16, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 8 }}>Product</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Category</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Stock</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Purchase (TZS)</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Sell (TZS)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                No products match this filter.
              </td>
            </tr>
          ) : (
            rows.map((p) => (
              <tr key={p.id}>
                <td style={{ padding: 8 }}>{p.name}</td>
                <td style={{ padding: 8 }}>{p.category || '—'}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{p.quantity}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{Number(p.purchasingPrice || 0).toLocaleString()}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{Number(p.sellingPrice || 0).toLocaleString()}</td>
              </tr>
            ))
          )}
        </tbody>
        {rows.length > 0 ? (
          <tfoot>
            <tr style={{ background: '#1e3a5f', color: '#f8fafc' }}>
              <td colSpan={2} style={{ padding: 10, fontWeight: 700 }}>
                Totals <span style={{ fontWeight: 500, opacity: 0.9 }}>(filtered)</span>
              </td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>{invTotals.qty.toLocaleString()}</td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>{Math.round(invTotals.atCost).toLocaleString()}</td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>{Math.round(invTotals.atSell).toLocaleString()}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
      <div style={{ marginTop: 20 }}>
        <button
          type="button"
          onClick={() => downloadCsv('inventory-report.csv', [exportHead, ...exportRowsWithTotals])}
          style={{ marginRight: 10 }}
        >
          Export to Excel
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              await exportTableToPdf('Inventory Report', exportHead, exportRowsWithTotals);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          }}
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
};

export default InventoryReportPage;
